import { captureException, withScope } from "@sentry/react";
import compact from "lodash/compact";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getSimRoamingCallPackageUsage,
  useGetESimInfo,
  useGetRoaming,
  useGetSimCustomerActions,
} from "@/services/api";
import {
  ESimInfo,
  GetRoamingPackageUsageResponse,
  RoamingInfo,
  SimCardDetail,
  SimCustomerInfoCreditClass,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { isUnscannedEsimInfo } from "@/utils/deviceUtils";
import {
  getProductCategory,
  getProductIdFromSim,
  getSimIdsNeedingRoamingCallPackageUsage,
} from "@/utils/productMatrix";

import { useAlert } from "./useAlert";
import { useLocalStorageFlag } from "./useLocalStorageFlag";

export interface ExtendedSimData {
  roamingInfo?: RoamingInfo[];
  esimInfo?: ESimInfo;
  roamingCallPackageUsage?: GetRoamingPackageUsageResponse;
}

export interface ExtendedSimCardDetail extends SimCardDetail {
  roamingInfo?: RoamingInfo[];
  esimInfo?: ESimInfo;
  roamingCallPackageUsage?: GetRoamingPackageUsageResponse;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}

export interface ExtendedSimCardDetailWithEsimInfo
  extends ExtendedSimCardDetail {
  matchingId: string;
  esimInfo: ESimInfo;
}

/**
 * This hook takes a list of sims and extends these by fetching esim and roaming info to result in a list of ExtendedSims
 * @param sims list of basic sims to extend
 * @param refetchInterval an optional interval for the api requests to refetch all data
 */
export const useExtendedSims = (
  sims: SimCardDetail[],
  refetchInterval?: false | number,
) => {
  const showAlert = useAlert();
  const { t } = useTranslation();
  const {
    shouldShowError: shouldShowRoamingError,
    setErrorFired: setRoamingErrorFired,
  } = useLocalStorageFlag("roamingErrorFiredAt");
  const {
    shouldShowError: shouldShowEsimError,
    setErrorFired: setEsimErrorFired,
  } = useLocalStorageFlag("esimErrorFiredAt");

  const [extendedSimData, setExtendedSimData] = useState<{
    [key: string]: ExtendedSimData;
  }>({});
  const [isLoadingCallPackage, setIsLoadingCallPackage] = useState(false);

  // Account-level credit class (needed to skip international-call usage fetches for class C).
  const { data: simCustomerActions } = useGetSimCustomerActions();
  const creditClass = simCustomerActions?.creditClass;

  // Included roaming is only relevant for mobile products; skip data/home SIMs.
  const roamingSimIds = useMemo(
    () =>
      compact(
        sims
          .filter(
            (sim) => getProductCategory(getProductIdFromSim(sim)) === "mobile",
          )
          .map((sim) => sim.simId),
      ),
    [sims],
  );

  const { isFetching: isLoadingRoaming } = useGetRoaming(
    { simIds: roamingSimIds },
    {
      query: {
        enabled: roamingSimIds.length > 0,
        refetchInterval,
        refetchOnMount: true,
        onSuccess: (roamingInfos) => {
          const newExtendedSimData = {
            ...extendedSimData,
          };

          roamingSimIds.forEach((simCardID) => {
            const simKey = String(simCardID);
            newExtendedSimData[simKey] = {
              ...(newExtendedSimData[simKey] ?? {}),
              // API volumes are already MB — same unit as the roaming tab.
              roamingInfo:
                roamingInfos?.filter(({ simId }) => simId === simCardID) ?? [],
            };
          });
          setExtendedSimData(newExtendedSimData);
        },
        onError: () => {
          if (shouldShowRoamingError()) {
            showAlert({
              type: AlertTypes.info,
              text: t("Error.roamingError"),
            });
            setRoamingErrorFired();
          }
        },
      },
    },
  );

  // Finite included international-call allowances (e.g. Flat Mobile) need a usage fetch.
  const callPackageSimIds = useMemo(
    () => getSimIdsNeedingRoamingCallPackageUsage(sims, creditClass),
    [sims, creditClass],
  );
  const callPackageSimIdsKey = callPackageSimIds.join(",");

  useEffect(() => {
    if (callPackageSimIds.length === 0) {
      setIsLoadingCallPackage(false);

      return;
    }

    let cancelled = false;
    setIsLoadingCallPackage(true);

    Promise.allSettled(
      callPackageSimIds.map((simId) =>
        getSimRoamingCallPackageUsage({ simId }).then((data) => ({
          simId,
          data,
        })),
      ),
    ).then((results) => {
      if (cancelled) {
        return;
      }

      const fulfilled = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          // TODO: replace by better error handler
          // eslint-disable-next-line no-console -- redacted category only, no simId/raw reason (see Sentry capture below for diagnostics)
          console.error(
            "[useExtendedSims] roamingCallPackageUsage fetch failed",
          );
          withScope((scope) => {
            scope.setTag("section", "portal");
            scope.setTag("domain", "useExtendedSims");
            scope.setTag("endpoint", "roamingCallPackageUsage");
            scope.setExtra("simId", callPackageSimIds[index]);
            captureException(result.reason);
          });
        }
      });

      setExtendedSimData((prev) => {
        const next = { ...prev };
        fulfilled.forEach(({ simId, data }) => {
          const simKey = String(simId);
          next[simKey] = {
            ...(next[simKey] ?? {}),
            roamingCallPackageUsage: data,
          };
        });

        return next;
      });

      setIsLoadingCallPackage(false);
    });

    return () => {
      cancelled = true;
    };
    // callPackageSimIdsKey keeps the dependency stable across identical id lists
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callPackageSimIdsKey]);

  // get a list of SimIDs where esim infos are missing
  // if `remainingDays` is not set, the SIM is not ready for further processing, so we don't need additional data for now
  const esims = compact(
    sims.filter(
      (sim) =>
        sim.simId !== undefined &&
        sim.remainingDays !== undefined &&
        sim.simType === "eSIM",
    ),
  );
  const missingEsimsIDs = useMemo(
    () => compact(esims.map((sim) => sim.simId)),
    [esims],
  );

  const { isFetching: isLoadingEsim } = useGetESimInfo(
    { simIds: missingEsimsIDs },
    {
      query: {
        enabled: missingEsimsIDs.length > 0,
        refetchInterval,
        refetchOnMount: true,
        onSuccess: (esimInfos) => {
          const newExtendedSimData = {
            ...extendedSimData,
          };
          missingEsimsIDs.forEach((simCardID) => {
            const simKey = String(simCardID);
            newExtendedSimData[simKey] = {
              ...(newExtendedSimData[simKey] ?? {}),
              esimInfo:
                esimInfos?.find(({ simId }) => simId === simCardID) ?? {},
            };
          });
          setExtendedSimData(newExtendedSimData);
        },
        onError: () => {
          if (shouldShowEsimError()) {
            showAlert({
              type: AlertTypes.info,
              text: t("Error.eSimError"),
            });
            setEsimErrorFired();
          }
        },
      },
    },
  );

  // enrich the given sims and make them extended sims
  const extendedSims: ExtendedSimCardDetail[] = sims.map((simCard) => ({
    ...simCard,
    ...(extendedSimData[String(simCard.simId)] ?? {}),
    creditClass,
  }));

  const isLoading = isLoadingRoaming || isLoadingEsim || isLoadingCallPackage;

  // determine whether there are unscanned E-SIMs
  const hasUnscannedEsims = extendedSims.some((simCard) =>
    simCard.esimInfo ? isUnscannedEsimInfo(simCard.esimInfo) : false,
  );

  return { extendedSims, isLoading, hasUnscannedEsims, creditClass };
};
